import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { AB_TESTS_DATA, AB_CHART_DATA, AB_CONFIDENCE_DATA } from '../data/abTests';
import Badge from '../components/ui/Badge';
import Skeleton from '../components/ui/Skeleton';
import styles from './ABTests.module.css';

class ABTests extends React.Component {
  constructor(props) {
    super(props);
    this.state = { loading: true };
  }

  componentDidMount() { setTimeout(() => this.setState({ loading: false }), 700); }

  render() {
    const { loading } = this.state;

    const ctrData = AB_CHART_DATA.labels.map((l, i) => ({ l, v: AB_CHART_DATA.data[i], col: AB_CHART_DATA.colors[i] }));
    const confData = AB_CONFIDENCE_DATA.labels.map((l, i) => ({ l, v: AB_CONFIDENCE_DATA.data[i] }));

    return (
      <>
        <div className={styles.pageHead}>
          <div>
            <div className={styles.pageTitle}>
              A/B Tests
              <span className={styles.soonBadge}>Coming soon</span>
            </div>
            <div className={styles.pageSub}>Statistical significance · lift analysis · variant comparison</div>
          </div>
          <button className={styles.newBtn} disabled>+ New Test</button>
        </div>

        <div className={styles.comingSoonWrap}>
        {/* Test cards */}
        {loading
          ? [0,1,2].map(i => (
              <div key={i} className={styles.card} style={{ marginBottom: 14 }}>
                <Skeleton variant="text" width={200} height={16} style={{ margin: '14px 16px 8px' }} />
                <Skeleton variant="rect" width="calc(100% - 32px)" height={100} style={{ margin: '0 16px 16px', borderRadius: 8 }} />
              </div>
            ))
          : AB_TESTS_DATA.map(t => (
              <div key={t.id} className={styles.card} style={{ marginBottom: 14 }}>
                <div className={styles.testHead}>
                  <div>
                    <div className={styles.testName}>{t.name}</div>
                    <div className={styles.testHyp}>{t.hyp}</div>
                  </div>
                  <div className={styles.testMeta}>
                    <Badge status={t.st === 'completed' ? 'completed' : 'running'} />
                    <div className={styles.confPill} style={{ background: parseFloat(t.conf) >= 95 ? 'var(--greenLo)' : 'var(--amberLo)', color: parseFloat(t.conf) >= 95 ? 'var(--greenHi)' : 'var(--amberHi)' }}>
                      {t.conf} confidence
                    </div>
                  </div>
                </div>
                <div className={styles.variants}>
                  <div className={styles.variant}>
                    <div className={styles.varLabel}>Variant A</div>
                    <div className={styles.varName}>{t.va}</div>
                  </div>
                  <div className={styles.vsChip}>vs</div>
                  <div className={styles.variant}>
                    <div className={styles.varLabel}>Variant B {t.winner === 'B' && <span className={styles.winBadge}>Winner</span>}</div>
                    <div className={styles.varName}>{t.vb}</div>
                  </div>
                  {t.lift !== '—' && (
                    <div className={styles.liftChip}>
                      <span className={styles.liftVal}>{t.lift}</span>
                      <span className={styles.liftLbl}>lift</span>
                    </div>
                  )}
                </div>
              </div>
            ))
        }

        {/* Charts */}
        <div className={styles.grid2}>
          <div className={styles.card}>
            <div className={styles.cardHead}>
              <div className={styles.cardTitle}>CTR Comparison</div>
              <div className={styles.cardSub}>All active tests · variant A vs B</div>
            </div>
            <div className={styles.cardBody}>
              <ResponsiveContainer width="100%" height={185}>
                <BarChart data={ctrData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b1)" vertical={false} />
                  <XAxis dataKey="l" tick={{ fill: 'var(--t3)', fontSize: 9 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: 'var(--t3)', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: 'var(--s3)', border: '1px solid var(--b2)', borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="v" radius={[3, 3, 0, 0]}>
                    {ctrData.map((entry, i) => <Cell key={i} fill={entry.col} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className={styles.card}>
            <div className={styles.cardHead}>
              <div className={styles.cardTitle}>Statistical Confidence</div>
              <div className={styles.cardSub}>% confidence per test</div>
            </div>
            <div className={styles.cardBody}>
              <ResponsiveContainer width="100%" height={185}>
                <BarChart data={confData} layout="vertical" margin={{ top: 8, right: 8, left: 60, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--b1)" horizontal={false} />
                  <XAxis type="number" domain={[0, 100]} tick={{ fill: 'var(--t3)', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis dataKey="l" type="category" tick={{ fill: 'var(--t2)', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: 'var(--s3)', border: '1px solid var(--b2)', borderRadius: 8, fontSize: 12 }} formatter={v => [v + '%', 'Confidence']} />
                  <Bar dataKey="v" radius={[0, 3, 3, 0]}>
                    {confData.map((e, i) => (
                      <Cell key={i} fill={e.v >= 95 ? 'rgba(79,155,111,.6)' : 'rgba(168,136,58,.55)'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        </div>
      </>
    );
  }
}

export default ABTests;
